import { Observable, of, throwError, Subject } from 'rxjs';
import { ajax, AjaxResponse } from 'rxjs/ajax';
import { map, retryWhen, mergeMap, switchMap, take, delay } from 'rxjs/operators';

import {
  GeneralModel,
  ClientModel,
  ProjectNewModel,
  UserModel,
  AccessControlSystemModel,
  WorkerModel,
  BadgePrintingSystemModel,
  CertificationModel,
  FileModel,
  ConsentFormModel,
  BadgeModel,
  StatisticsModel,
  TrainingModel,
  PaymentModel,
  InvoiceModel,
  ProcoreModel,
  SearchModel,
  FcaLearningModel,
  TurnstileScheduleModel,
  ProductModel,
  DownloadsModel,
} from '../models';
import { ENV, LANG } from '../../constants';
import { parseQuery, sanitizePaginationQuery } from '../../utils/generalUtils';
import { AuthService } from './AuthService';
import { IProcoreSaveProjectMapping, IProcoreSaveVendorMapping } from '../models/procore';
import { IFcaLearningGroup } from 'modules/models/fcaLearning';
import { ICommunicationsResponse } from 'modules/models/communications';
import { IChangeLogEntity } from 'modules/models/audit-trail';
import { ProductStatus } from 'modules/models/product';

interface IOptionRequest {
  method: string;
  body?: any;
  data?: any;
  headers?: { [key: string]: any };
}

interface IMock {
  isError: boolean;
  response: Object;
}

export class ApiService {
  public static AuthService = AuthService;
  private http = ajax;
  private apiUrl: string = ENV.API.URL;
  private securityApiUrl: string = ENV.API.SECURITY_URL;
  private configurationApiUrl: string = ENV.API.CONFIGURATION_URL;
  private learningApiUrl: string = ENV.API.LEARNING_URL;
  private changeLogApiUrl: string = ENV.API.CHANGE_LOG_URL;
  private maxRetries: number = ENV.API.MAX_RETRIES;
  private retryTimeout: number = ENV.API.RETRY_TIMEOUT;

  // Projects
  public getProject(id: string): Observable<ProjectNewModel.IProject> {
    return this.protectedRequest(`projects/${id}`, { method: 'GET' });
  }

  public getProjectSummary(id: string): Observable<ProjectNewModel.IProject> {
    return this.protectedRequest(`projects/${id}/summary`, { method: 'GET' });
  }

  public getProjectList(query: GeneralModel.IQueryParams): Observable<GeneralModel.IPagination<ProjectNewModel.IProject>> {
    return this.protectedRequest(`projects?${parseQuery(sanitizePaginationQuery(query))}`, { method: 'GET' });
  }

  public updateProject(project: Partial<ProjectNewModel.IProject>): Observable<ProjectNewModel.IProject> {
    return this.protectedRequest(`projects/${project.id}`, { method: 'PUT', body: project });
  }

  public getProjectClientList(id: string, query: GeneralModel.IQueryParams): Observable<GeneralModel.IPagination<ClientModel.IClientProject>> {
    return this.protectedRequest(`projects/${id}/companies?${parseQuery(sanitizePaginationQuery(query))}`, { method: 'GET' });
  }

  public getClientListForMultipleProjects(query: GeneralModel.IQueryParams): Observable<GeneralModel.IPagination<ClientModel.IClientProject>> {
    return this.protectedRequest(`projects/companies?${parseQuery(sanitizePaginationQuery(query))}`, { method: 'GET' });
  }

  public getProjectUserList({ id, query }: { id: string; query: GeneralModel.IQueryParams }): Observable<GeneralModel.IPagination<Partial<UserModel.IUser>>> {
    return this.protectedRequest(`projects/${id}/users?${parseQuery(sanitizePaginationQuery(query))}`, { method: 'GET' });
  }

  public getProjectAddresses(projectId: string, query: string): Observable<any> {
    return this.protectedRequest(`projects/${projectId}/addresses?${query}`, { method: 'GET' });
  }

  public getUserClientList(id: string): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest(`Users/${id}/Companies`, { method: 'GET' });
  }

  public getProjectAccessControlSystemList({
    id,
    query,
  }: {
    id: string;
    query?: GeneralModel.IQueryParams;
  }): Observable<AccessControlSystemModel.IProjectAccessControlSystemByLocation[]> {
    return this.protectedRequest(`projects/${id}/accessControlSystems?${parseQuery(sanitizePaginationQuery(query))}`, { method: 'GET' });
  }

  public getProjectBadgePrintingSystemList({
    id,
    page = 1,
    limit = 30,
  }: {
    id: string;
    page?: number;
    limit?: number;
  }): Observable<GeneralModel.IPagination<BadgePrintingSystemModel.IBadgePrintingSystem>> {
    return this.protectedRequest(`projects/${id}/badgePrintingSystems?pageNumber=${page}&pageSize=${limit}`, { method: 'GET' });
  }

  public getProjectTypes(): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest(`projectTypes`, { method: 'GET' });
  }

  public assignClientProject(id: string, list: { id: string; role: ProjectNewModel.CompanyRole }[], sponsorId: string): Observable<ClientModel.IClient[]> {
    return this.protectedRequest(`projects/${id}/companies/assign`, { method: 'POST', body: { companies: list, sponsorId } });
  }

  public assignUserProject(id: string, list: ProjectNewModel.IProjectAssignUser[]): Observable<UserModel.IUser[]> {
    return this.protectedRequest(`projects/${id}/users/assign`, { method: 'POST', body: list });
  }

  public assignAcsProject(
    projectId: string,
    acs: AccessControlSystemModel.IProjectAccessControlSystem
  ): Observable<AccessControlSystemModel.IAccessControlSystem[]> {
    return this.protectedRequest(`projects/${projectId}/accessControlSystems`, { method: 'POST', body: acs });
  }

  public assignBadgePrintingSystemProject(
    projectId: string,
    list: BadgePrintingSystemModel.IBadgePrintingSystemUpdateDate[]
  ): Observable<BadgePrintingSystemModel.IBadgePrintingSystem[]> {
    return this.protectedRequest(`projects/${projectId}/badgePrintingSystems/assign`, { method: 'POST', body: list });
  }

  public unAssignAccessControlSystemProject(projectId: string, acsId: string): Observable<any> {
    return this.protectedRequest(`projects/${projectId}/accessControlSystems/${acsId}`, { method: 'DELETE' });
  }

  public unAssignBadgePrintingSystemProject(projectId: string, bpsId: string): Observable<BadgePrintingSystemModel.IBadgePrintingSystem> {
    return this.protectedRequest(`projects/${projectId}/badgePrintingSystems/${bpsId}/unassign`, { method: 'POST' });
  }

  public getProjectAccessControlSystem(projectId: string, acsId: string): Observable<AccessControlSystemModel.IProjectAccessControlSystem> {
    return this.protectedRequest(`projects/${projectId}/accessControlSystems/${acsId}`, { method: 'GET' });
  }

  public getProjectAccessControlSystemVerifyConfiguration(id: string): Observable<any> {
    return this.protectedRequest(`projectAccessControlSystems/${id}/verifyConfig`, { method: 'POST' });
  }

  public updateProjectAccessControlSystem(
    projectId: string,
    acsId: string,
    acs: AccessControlSystemModel.IProjectAccessControlSystem
  ): Observable<AccessControlSystemModel.IProjectAccessControlSystem> {
    return this.protectedRequest(`projects/${projectId}/accessControlSystems/${acsId}`, { method: 'PUT', body: acs });
  }

  public updateBadgePrintingSystemProjectDate(
    projectId: string,
    bpsId: string,
    shippingDate: string
  ): Observable<BadgePrintingSystemModel.IBadgePrintingSystem> {
    return this.protectedRequest(`projects/${projectId}/badgePrintingSystems/${bpsId}`, { method: 'PUT', body: { shippingDate } });
  }

  public archiveProject(id: string): Observable<any> {
    return this.protectedRequest(`projects/${id}/archive`, { method: 'POST' });
  }

  public unarchiveProject(id: string): Observable<any> {
    return this.protectedRequest(`projects/${id}/unarchive`, { method: 'POST' });
  }

  public getProjectClientHirearchyList(id: string): Observable<GeneralModel.IPagination<ClientModel.IHirearchyClientProject>> {
    return this.protectedRequest(`projects/${id}/companies/hierarchy?clientSidePaging=true`, { method: 'GET' });
  }

  public getDraftProject(id: string): Observable<ProjectNewModel.IProject> {
    return this.protectedRequest(`draftProjects/${id}`, { method: 'GET' });
  }

  public saveProject(project: Partial<ProjectNewModel.IProject>): Observable<ProjectNewModel.IProject> {
    return this.protectedRequest('draftProjects', { method: 'POST', body: project });
  }

  public updateDraftProject(project: Partial<ProjectNewModel.IProject>): Observable<ProjectNewModel.IProject> {
    return this.protectedRequest(`draftProjects/${project.id}`, { method: 'PUT', body: project });
  }

  public sendApproveProject(id: string): Observable<any> {
    return this.protectedRequest(`draftProjects/${id}/sendForApproval`, { method: 'POST' });
  }

  public approveProject(id: string): Observable<any> {
    return this.protectedRequest(`draftProjects/${id}/approve`, { method: 'POST' });
  }

  public addCustomBadgeFields(id: string, badgeFields: any, url: string): Observable<any> {
    return this.protectedRequest(`${url}/${id}/badgeTemplateMiscellaneousfields`, { method: 'POST', body: badgeFields });
  }

  public retireBadgeTemplate(
    projectId: string,
    badgeTemplateId: string,
    url: string,
    replacedById?: string,
    removeExceptionsFromBadges?: boolean
  ): Observable<any> {
    return this.protectedRequest(`${url}/${projectId}/badgeTemplates/${badgeTemplateId}/retire`, {
      method: 'PUT',
      body: { replacedById: replacedById || '', removeExceptionsFromBadges },
    });
  }

  public updateBadgeTemplateStatus(projectId: string, badgeTemplateId: string, url: string): Observable<any> {
    return this.protectedRequest(`${url}/${projectId}/badgeTemplates/${badgeTemplateId}/setDefault`, { method: 'PUT' });
  }

  public updateProjectBadgeTemplate(url: string, projectId: string, payload: any): Observable<any> {
    return this.protectedRequest(`${url}/${projectId}/badgeTemplates/${payload.id}`, {
      method: 'PUT',
      body: payload,
    });
  }

  public fetchCustomBadgeFields(id: string, url: string): Observable<any> {
    return this.protectedRequest(`${url}/${id}/badgeTemplateMiscellaneousfields`, { method: 'GET' });
  }

  public deleteProject(id: string): Observable<any> {
    return this.protectedRequest(`draftProjects/${id}`, { method: 'DELETE' });
  }

  public getProjectCategories(): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest('categories', { method: 'GET' });
  }

  public getFcaNae(): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest('naes', { method: 'GET' });
  }

  public getFcaRegion(): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest('regions', { method: 'GET' });
  }

  public getSizeCategories(): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest('sizeCategories', { method: 'GET' });
  }

  public getCertifications(): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest('certifications', { method: 'GET' });
  }

  public getTrainings(): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest('trainings', { method: 'GET' });
  }

  public getCourses(projectId): Observable<FcaLearningModel.ICourse[]> {
    return this.learningRequest(`northpass/${projectId}/courses`, { method: 'GET' });
  }

  public postLearningGroupsSearch(body): Observable<{ paging: any; records: IFcaLearningGroup[] }> {
    return this.learningRequest(`learningGroups/search`, { method: 'POST', body: body });
  }

  public updateLearningGroupsBulk(body): Observable<IFcaLearningGroup[]> {
    return this.learningRequest(`learningGroups/learningGroupsBulk`, { method: 'POST', body: body });
  }

  public getWorkerCourseList(id, query): Observable<FcaLearningModel.IWorkerCourseListResponse> {
    return this.learningRequest(`northpass/workers/${id}/courses?${parseQuery(sanitizePaginationQuery(query))}`, { method: 'GET' });
  }

  public getLearnerWorker(workerId, projectId, companyId): Observable<FcaLearningModel.ILearnerWorker> {
    // return this.learningRequest(`workers/${workerId}/project/${projectId}/learner`, { method: 'GET' });
    return this.learningRequest(`workers/${workerId}/project/${projectId}/company/${companyId}/learner`, { method: 'GET' });
  }

  public getBillingTiers(): Observable<ProjectNewModel.IBillingTier[]> {
    return this.protectedRequest('billingTiers', { method: 'GET' });
  }

  public getManagedByOptionsList(): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest('projects/managedBy', { method: 'GET' });
  }

  // Billing Rules/Model

  public getProjectBillingModelPricing(id: string, query?: GeneralModel.IQueryParams): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest(`projects/${id}/projectBillingModelPricing?${query ? parseQuery(sanitizePaginationQuery(query)) : ''}`, {
      method: 'GET',
    });
  }

  public getProjectBillingEngineRules(id: string, query?: GeneralModel.IQueryParams): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest(`projects/${id}/billingEngineRules?${query ? parseQuery(sanitizePaginationQuery(query)) : ''}`, { method: 'GET' });
  }

  public getProjectBillingEngineRulesPricing(id: string, query?: GeneralModel.IQueryParams): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest(`projects/${id}/billingEngineRulePricingAgreementInfo?${query ? parseQuery(sanitizePaginationQuery(query)) : ''}`, {
      method: 'GET',
    });
  }

  public getProjectProductBundles(id: string): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest(`projects/${id}/productBundles`, { method: 'GET' });
  }

  public addBillingEngineRules(body): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest(`billingEngineRules`, { method: 'POST', body: body });
  }

  public addBillingEngineProductBundles(body): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest(`productBundles`, { method: 'POST', body: body });
  }

  public updateBillingEngineProductBundle(body): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest(`productBundles/${body.id}`, { method: 'PUT', body: body });
  }

  public retireBillingEngineProductBundle(id): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest(`productBundles/${id}/ChangeStatus`, { method: 'PUT', body: { toStatus: ProductStatus.Retired } });
  }

  public updateBillingEngineProductRule(body): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest(`billingEngineRules/${body.id}`, { method: 'PUT', body: body });
  }

  public retireBillingEngineRule(id): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest(`billingEngineRules/${id}/ChangeStatus`, { method: 'PUT', body: { toStatus: ProductStatus.Retired } });
  }

  public updatePricingAgreementVisibility(projectId: string, body): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest(`projects/${projectId}/pricingAgreement/billingInfoVisibility`, { method: 'POST', body: { ...body } });
  }

  public getProjectPricingAgreementExport(id: string, query: GeneralModel.IQueryParams): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest(`projects/${id}/pricingAgreement/export?${parseQuery(sanitizePaginationQuery(query))}`, { method: 'POST' });
  }

  public getProjectBillingEngineRulesCheck(id: string, body): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest(`projects/${id}/billingEngineRules/check`, { method: 'POST', body: { ...body } });
  }

  public getProjectWorkerList({
    id,
    query,
    isV2 = false,
  }: {
    id: string;
    query: GeneralModel.IQueryParams;
    isV2?: boolean;
  }): Observable<GeneralModel.IPagination<WorkerModel.IWorkerProject>> {
    return this.protectedRequest(`projects/${id}/workers${isV2 ? '/v2' : ''}?${parseQuery(sanitizePaginationQuery(query))}`, { method: 'GET' });
  }

  public assignWorkerProject(projectId: string, list: { id: string; location: string }[]): Observable<WorkerModel.IWorker[]> {
    return this.protectedRequest(`projects/${projectId}/workers/assign`, { method: 'POST', body: list });
  }

  public unassignWorkerProject(projectId: string, workerId: string, companyId: string): Observable<any> {
    return this.protectedRequest(`projects/${projectId}/workers/${workerId}/company/${companyId}/unassign`, { method: 'POST', body: {} });
  }

  public assignLearnerWorkerBulk(body): Observable<FcaLearningModel.ILearnerWorker[]> {
    return this.learningRequest(`workers/learnerworkerbulk`, { method: 'POST', body: body });
  }

  public exemptLearnerWorkerBulk(projectId, body): Observable<FcaLearningModel.ILearnerWorker[]> {
    return this.learningRequest(`workers/${projectId}/bulkExemptedCourses`, { method: 'POST', body: body });
  }

  public getProjectBadgeResources(
    projectId: string,
    fileNames: object,
    subcontractorBadgeTemplateMatchesGeneralContractor?: boolean
  ): Observable<ProjectNewModel.IProjectBadgeFilesResource> {
    return this.protectedRequest(`draftProjects/${projectId}/uploadBadgeTemplates`, {
      method: 'POST',
      body: { ...fileNames, subcontractorBadgeTemplateMatchesGeneralContractor },
    });
  }

  public addProjectBadgeTemplate(projectId: string, templates: object, url: string): Observable<any> {
    return this.protectedRequest(`${url}/${projectId}/uploadBadgeTemplates?skipOldBadgeSystem=true`, {
      method: 'POST',
      body: templates,
    });
  }

  public getBadgeTemplateExceptions(projectId: string, badgeTemplateId: string): Observable<any> {
    return this.protectedRequest(`projects/${projectId}/badgetemplates/${badgeTemplateId}/expirationexceptions`, {
      method: 'GET',
    });
  }

  public getVisitorBadgeList({ id, query }: { id: string; query: GeneralModel.IQueryParams }): Observable<GeneralModel.IPagination<BadgeModel.IBadgeVisitor>> {
    return this.protectedRequest(`projects/${id}/visitorbadges?${parseQuery(sanitizePaginationQuery(query))}`, { method: 'GET' });
  }

  public createBadgeVisitors(projectId: string, number: number): Observable<any> {
    return this.protectedRequest(`projects/${projectId}/visitorbadges/create`, { method: 'POST', body: { badgeCount: number } });
  }

  public getBadgeVisitorEntity(projectId: string): Observable<string[]> {
    return this.protectedRequest(`projects/${projectId}/visitorbadges/entities`, { method: 'GET' });
  }

  public searchProject(query: GeneralModel.IQueryParams): Observable<ProjectNewModel.IProject[]> {
    return this.protectedRequest(`projects/search?${parseQuery(query)}`, { method: 'GET' });
  }

  public getProjectClientSummary(projectId: string, clientId: string): Observable<ClientModel.IClient> {
    return this.protectedRequest(`projects/${projectId}/companies/${clientId}/summary`, { method: 'GET' });
  }

  public updateClientProjectTaxCondition(projectId: string, taxCondition: ProjectNewModel.IClientTaxCondition): Observable<any> {
    return this.protectedRequest(`projects/${projectId}/companies/updateTaxExemptionStatus`, { method: 'PUT', body: taxCondition });
  }

  public updateWelcomeKit(payload: { projectWelcomeKitRequests: { projectId: string; companyId: string }[] }): Observable<any> {
    return this.protectedRequest(`projects/transitionStatus`, { method: 'PUT', body: payload });
  }

  public getTimeZones(): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest('timeZones', { method: 'GET' });
  }

  public updateProjectPaymentMethod(projectId: string, paymentMethodId: string): Observable<any> {
    return this.protectedRequest(`projects/${projectId}/changeCreditCard`, { method: 'PUT', body: { paymentMethodId } });
  }

  public getProjectCertificationGroups(projectId: string, query?: GeneralModel.IQueryParams): Observable<any> {
    return this.protectedRequest(`projects/${projectId}/certificationGroups${query ? `?${parseQuery(sanitizePaginationQuery(query))}` : ''}`, {
      method: 'GET',
    });
  }

  public getProjectTrainingGroups(projectId: string, query?: GeneralModel.IQueryParams): Observable<any> {
    return this.protectedRequest(`projects/${projectId}/trainingGroups${query ? `?${parseQuery(sanitizePaginationQuery(query))}` : ''}`, {
      method: 'GET',
    });
  }

  public updatePendingWelcomeClientNotes(payload: any): Observable<any> {
    return this.protectedRequest(`projects/${payload.projectId}/companies/${payload.clientId}/notes`, { method: 'PUT', body: { notes: payload.notes } });
  }

  // Invoice
  public getInvoiceList(query: GeneralModel.IQueryParams, entityId?: string, url?: boolean): Observable<GeneralModel.IPagination<InvoiceModel.IInvoice>> {
    return this.protectedRequest(`${url ? `${url}/${entityId}/` : ''}invoices?${parseQuery(sanitizePaginationQuery(query))}`, { method: 'GET' });
  }

  public getServiceTypeList(): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest(`billableServices`, { method: 'GET' });
  }

  public saveInvoice(invoice: InvoiceModel.IInvoice): Observable<InvoiceModel.IInvoice> {
    return this.protectedRequest(`invoices`, { method: 'POST', body: invoice });
  }

  public editInvoice(id: string, invoice: InvoiceModel.IInvoice): Observable<any> {
    return this.protectedRequest(`invoices/${id}`, { method: 'PUT', body: invoice });
  }

  public getInvoiceSummary(id: string): Observable<InvoiceModel.IInvoice> {
    return this.protectedRequest(`invoices/${id}/summary`, { method: 'GET' });
  }

  public getInvoice(id: string): Observable<InvoiceModel.IInvoice> {
    return this.protectedRequest(`invoices/${id}`, { method: 'GET' });
  }

  public deleteInvoice(id: string): Observable<any> {
    return this.protectedRequest(`invoices/${id}`, { method: 'DELETE' });
  }

  public markInvoiceAsPaid(id: string, reason: string): Observable<any> {
    return this.protectedRequest(`invoices/${id}/markAsPaid`, { method: 'POST', body: { PaymentDescription: reason } });
  }

  public markInvoiceAsVoid(id: string): Observable<any> {
    return this.protectedRequest(`invoices/${id}/markAsVoid`, { method: 'POST' });
  }

  public payInvoice(id: string): Observable<any> {
    return this.protectedRequest(`invoices/${id}/pay`, { method: 'POST' });
  }

  public confirmInvoice(id: string): Observable<any> {
    return this.protectedRequest(`invoices/${id}/confirm`, { method: 'POST' });
  }

  public downloadInvoice(id: string): Observable<FileModel.IDownloadFile> {
    return ApiService.AuthService.getSession().pipe(mergeMap(token => this.downloadFile(`invoices/${id}/download`, 'pdf', token.getIdToken().getJwtToken())));
  }

  public updateUpcomingInvoice(data: any): Observable<any> {
    return this.protectedRequest(`Invoices/updateUpcomingInvoice`, { method: 'POST', body: data });
  }

  public updateBillingEngineUpcomingInvoice(data: any): Observable<any> {
    return this.protectedRequest(`Invoices/updateBillingEngineUpcomingInvoice`, { method: 'POST', body: data });
  }

  // Company
  public getClient(id: string): Observable<ClientModel.IClient> {
    return this.protectedRequest(`companies/${id}`, { method: 'GET' });
  }

  public getCompanyUser(companyId: string, companyUserId: string): Observable<UserModel.IUser> {
    return this.protectedRequest(`companies/${companyId}/users/${companyUserId}`, { method: 'GET' });
  }

  public updateCompanyUser(companyId: string, companyUserId: string, user: UserModel.IUser): Observable<UserModel.IUser> {
    return this.protectedRequest(`companies/${companyId}/users/${companyUserId}`, { method: 'PUT', body: user });
  }

  public getClientList(query: GeneralModel.IQueryParams): Observable<GeneralModel.IPagination<ClientModel.IClient>> {
    return this.protectedRequest(`companies?${parseQuery(sanitizePaginationQuery(query))}`, {
      method: 'GET',
    });
  }

  public getClientUserList({
    id,
    page = 1,
    limit = 30,
    projectId = '',
    clientSidePaging = false,
    isForInviteClient = false,
  }: {
    id: string;
    page?: number;
    limit?: number;
    projectId?: string;
    clientSidePaging?: boolean;
    isForInviteClient?: boolean;
  }): Observable<GeneralModel.IPagination<Partial<UserModel.IUser>>> {
    return this.protectedRequest(
      `companies/${id}/users?pageNumber=${page}&pageSize=${limit}&projectId=${projectId}${clientSidePaging ? '&clientSidePaging=true' : ''}${
        isForInviteClient ? '&isForInviteClient=true' : ''
      }`,
      {
        method: 'GET',
      }
    );
  }

  public getClientProjectList(id: string, query: GeneralModel.IQueryParams): Observable<GeneralModel.IPagination<Partial<ProjectNewModel.IProject>>> {
    return this.protectedRequest(`companies/${id}/projects?${parseQuery(query)}`, { method: 'GET' });
  }

  public getClientWorkerList(id: string, query: GeneralModel.IQueryParams): Observable<GeneralModel.IPagination<Partial<WorkerModel.IWorker>>> {
    return this.protectedRequest(`companies/${id}/workers?${parseQuery(sanitizePaginationQuery(query))}`, { method: 'GET' });
  }

  public getProjectCompanyGroups(id: string, companyIds: Array<string>): Observable<GeneralModel.IPagination<Partial<ProjectNewModel.IProject>>> {
    return this.securityRequest(`Project/${id}/CompanyGroups`, { method: 'POST', body: { companyIds } });
  }

  public getClientDetail(id: string): Observable<ClientModel.IClient> {
    return this.protectedRequest(`companies/${id}`, { method: 'GET' });
  }

  public getClientSummary(id: string): Observable<ClientModel.IClient> {
    return this.protectedRequest(`companies/${id}/summary`, { method: 'GET' });
  }

  public saveUser(companyId: string, user: UserModel.IUser): Observable<UserModel.IUser> {
    return this.protectedRequest(`companies/${companyId}/users`, { method: 'POST', body: user });
  }

  public updateClient(client: ClientModel.IClient): Observable<ClientModel.IClient> {
    return this.protectedRequest(`companies/${client.id}`, { method: 'PUT', body: client });
  }

  public archiveClient(id: string): Observable<any> {
    return this.protectedRequest(`companies/${id}/archive`, { method: 'POST' });
  }

  public unarchiveClient(id: string): Observable<any> {
    return this.protectedRequest(`companies/${id}/unarchive`, { method: 'POST' });
  }

  public searchClient(query: GeneralModel.IQueryParams): Observable<ClientModel.IClient[]> {
    return this.protectedRequest(`companies/search?${parseQuery(query)}`, { method: 'GET' });
  }

  public getWorkerProjectByCompany(id: string, query: GeneralModel.IQueryParams): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest(`companies/${id}/workers/projectNames?${parseQuery(query)}`);
  }

  public getDraftClient(id: string): Observable<ClientModel.IClient> {
    return this.protectedRequest(`draftCompanies/${id}`, { method: 'GET' });
  }

  public getDraftClientSummary(id: string): Observable<ClientModel.IClient> {
    return this.protectedRequest(`draftCompanies/${id}/summary`, { method: 'GET' });
  }

  public getClientHierarchy(id: string, query: GeneralModel.IQueryParams): Observable<ClientModel.IBranch> {
    return this.protectedRequest(`Companies/${id}/hierarchy?${parseQuery(query)}`, { method: 'GET' });
  }

  public updateClientHierarchy(id: string, hierarchy: any): Observable<ClientModel.IClient> {
    return this.protectedRequest(`Companies/${id}/hierarchy`, { method: 'POST', body: hierarchy });
  }

  public inviteCompanyAsABranch(id: string, hierarchy: any): Observable<ClientModel.IClient> {
    return this.protectedRequest(`DraftCompanies/${id}/InviteCompanyAsABranch`, { method: 'POST', body: hierarchy });
  }

  public saveClient(client: ClientModel.IClient): Observable<ClientModel.IClient> {
    return this.protectedRequest('draftCompanies', { method: 'POST', body: client });
  }

  public sendApproveClient(id: string): Observable<any> {
    return this.protectedRequest(`draftCompanies/${id}/sendForApproval`, { method: 'POST' });
  }

  public approveClient(id: string): Observable<any> {
    return this.protectedRequest(`draftCompanies/${id}/approve`, { method: 'POST' });
  }

  public inviteDraftClient(client: ClientModel.IClient): Observable<ClientModel.IClient> {
    return this.protectedRequest(`draftCompanies/invite?excludeShellCompanies=true`, { method: 'POST', body: client });
  }

  public updateDraftClient(client: ClientModel.IClient): Observable<ClientModel.IClient> {
    return this.protectedRequest(`draftCompanies/${client.id}`, { method: 'PUT', body: client });
  }

  public deleteClient(id: string): Observable<any> {
    return this.protectedRequest(`draftCompanies/${id}`, { method: 'DELETE' });
  }

  public getMwbeTypes(): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest('MwbeTypes', { method: 'GET' });
  }

  public getTrades(): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest('Trades', { method: 'GET' });
  }

  public getConsentFormFields(
    consentFormFieldType?: number,
    companyIds?: string[],
    projectId?: string,
    includeOnlyGCsFields?: boolean,
    includeUsageCount?: boolean,
    includeListItems?: boolean
  ): Observable<ConsentFormModel.IConsentFormField[]> {
    return this.protectedRequest(
      `consentFormFields?${parseQuery({
        consentFormFieldType,
        'companyIds[0]': companyIds ? companyIds[0] : undefined,
        projectId,
        includeOnlyGCsFields,
        includeUsageCount,
        includeListItems,
      })}`,
      { method: 'GET' }
    );
  }

  public getRegularExpressions(): Observable<ConsentFormModel.IConsentFormField[]> {
    return this.protectedRequest(`regularExpressions`, { method: 'GET' });
  }

  public saveConsentFormFieldsBulk(payload?: any): Observable<ConsentFormModel.IConsentFormField[]> {
    return this.protectedRequest(`consentFormFields/bulk`, { method: 'POST', body: payload });
  }

  public updateConsentFormFieldById(id: string, payload?: any): Observable<ConsentFormModel.IConsentFormField> {
    return this.protectedRequest(`consentFormFields/${id}`, { method: 'PUT', body: payload });
  }

  public deleteConsentFormField(id: string): Observable<ConsentFormModel.IConsentFormField> {
    return this.protectedRequest(`consentFormFields/${id}`, { method: 'DELETE' });
  }

  public searchJointVentureCompanyUsers(companyId: string, query: GeneralModel.IQueryParams): Observable<UserModel.IUser[]> {
    return this.protectedRequest(`companies/${companyId}/users/search?${parseQuery(query)}`, { method: 'GET' });
  }

  public fetchFailedTransactions(companyId: string): Observable<UserModel.IUser[]> {
    return this.protectedRequest(`FailedTransactions/company/${companyId}`, { method: 'GET' });
  }

  public getMergeCompanySummary(id: string): Observable<any> {
    return this.protectedRequest(`merge/companies/${id}/summary`, { method: 'GET' });
  }

  public getMergeCompanyProjectSummary(id: string): Observable<any> {
    return this.protectedRequest(`merge/companies/${id}/projectsSummary`, { method: 'GET' });
  }

  public getMergeCompanyUsers(id: string): Observable<any> {
    return this.protectedRequest(`merge/companies/${id}/users?clientSidePaging=true`, { method: 'GET' });
  }

  public saveMergeCompany(payload: any): Observable<any> {
    return this.protectedRequest(`merge/companies`, { method: 'POST', body: payload });
  }

  public getMergeCompanyBillingSummary(id: string): Observable<any> {
    return this.protectedRequest(`merge/companies/${id}/billingSummary`, { method: 'GET' });
  }

  public getMergeCompanyInvoicesSummary(id: string, query?: GeneralModel.IQueryParams): Observable<any> {
    return this.protectedRequest(`merge/companies/${id}/invoiceSummary${query ? `?${parseQuery(sanitizePaginationQuery(query))}` : ''}`, { method: 'GET' });
  }

  public getMergeCompanyWorkers(id: string): Observable<any> {
    return this.protectedRequest(`merge/companies/${id}/workers?clientSidePaging=true`, { method: 'GET' });
  }

  public getMergeCompanyInvoices(id: string): Observable<any> {
    return this.protectedRequest(`merge/companies/${id}/invoices?clientSidePaging=true`, { method: 'GET' });
  }

  public getAllActiveClients(query: GeneralModel.IQueryParams): Observable<any> {
    return this.protectedRequest(`companies?${parseQuery(sanitizePaginationQuery(query))}`, { method: 'GET' });
  }

  // Workers
  public getWorker(id: string): Observable<WorkerModel.IWorker> {
    return this.protectedRequest(`workers/${id}`, { method: 'GET' });
  }

  // WorkerProfileCustomFields
  public getWorkerProfileCustomFields(data: any): Observable<any> {
    return this.protectedRequest(`Workers/WorkerProfileCustomFields`, { method: 'POST', body: data });
  }

  // WorkerProfileDefaultCustomFields
  public getWorkerProfileDefaultCustomFields(): Observable<any> {
    return this.protectedRequest(`Workers/WorkerProfileDefaultCustomFields`, { method: 'GET' });
  }

  public getWorkerActivityList(id: string, query: GeneralModel.IQueryParams): Observable<GeneralModel.IPagination<Partial<WorkerModel.IWorkerActivity>>> {
    return this.protectedRequest(
      `workers/${id}/activities?${parseQuery(
        sanitizePaginationQuery({
          ...query,
          sortType: 'DESC',
          sortingName: 'EventTimestamp',
          period: query?.period,
          projectId: query.projectId || undefined,
          useProjectTimezone: false,
        })
      )}`
    );
  }

  public getWorkerObservationList(id: string, query: GeneralModel.IQueryParams): Observable<GeneralModel.IPagination<Partial<WorkerModel.IWorkerObservation>>> {
    return this.protectedRequest(
      `workers/${id}/observations?${parseQuery(sanitizePaginationQuery({ ...query, period: query?.period, projectId: query.projectId || undefined }))}`
    );
  }

  public getWorkerList(query: GeneralModel.IQueryParams): Observable<GeneralModel.IPagination<WorkerModel.IWorker>> {
    // return this.protectedRequest(`workers?${parseQuery(sanitizePaginationQuery(query))}`, { method: 'GET' });
    return this.protectedRequest(`workers${query.status === 2 || query.status === 5 ? '/WorkersList' : ''}?${parseQuery(sanitizePaginationQuery(query))}`, {
      method: 'GET',
    });
  }

  public saveWorker(worker: WorkerModel.IWorker): Observable<WorkerModel.IWorker> {
    return this.protectedRequest('workers', { method: 'POST', body: worker });
  }

  public updateWorker(worker: WorkerModel.IWorker): Observable<WorkerModel.IWorker> {
    return this.protectedRequest(`workers/${worker.id}?version=v2`, { method: 'PUT', body: worker });
  }

  public deleteWorker(id: string, secretPassword: string): Observable<WorkerModel.IWorker> {
    return this.protectedRequest(`workers/deleteWorker`, { method: 'POST', body: { workerId: id, secretPassword } });
  }

  public transferWorker(body: any): Observable<any> {
    return this.protectedRequest(`workers/transfer`, { method: 'POST', body: body });
  }

  public transferWorkerV2(id: string, body: any): Observable<any> {
    return this.protectedRequest(`workers/${id}/transfer-v2`, { method: 'POST', body: body });
  }

  public getWorkerCertificationList(
    id: string,
    query: GeneralModel.IQueryParams
  ): Observable<GeneralModel.IPagination<CertificationModel.IWorkerCertification>> {
    return this.protectedRequest(`workers/${id}/certifications?${parseQuery(sanitizePaginationQuery(query))}`, { method: 'GET' });
  }

  public getWorkerTrainingList(id: string, query: GeneralModel.IQueryParams): Observable<GeneralModel.IPagination<TrainingModel.IWorkerTraining>> {
    return this.protectedRequest(`workers/${id}/trainings?${parseQuery(sanitizePaginationQuery(query))}`, { method: 'GET' });
  }

  public getWorkerProjectList(
    id: string,
    query: GeneralModel.IQueryParams = { page: 1, limit: 30 }
  ): Observable<GeneralModel.IPagination<ProjectNewModel.IWorkerProject>> {
    return this.protectedRequest(`workers/${id}/projects?${parseQuery(sanitizePaginationQuery(query))}`, { method: 'GET' });
  }

  public getWorkerTransferProjectList(workerId: string, companyId: string): Observable<ProjectNewModel.IWorkerProject[]> {
    return this.protectedRequest(`workers/${workerId}/transfer/projects?sourceCompanyId=${companyId}`, { method: 'GET' });
  }

  public getTransferCompaniesForWorkerTransfer(workerId: string, companyId: string): Observable<ProjectNewModel.IWorkerProject[]> {
    return this.protectedRequest(`workers/${workerId}/transfer/targetCompanies?sourceCompanyId=${companyId}&SortCriteria=name asc`, { method: 'GET' });
  }

  public getWorkerCertification(id: string, certId: string): Observable<CertificationModel.IWorkerCertification> {
    return this.protectedRequest(`workers/${id}/certifications/${certId}`, { method: 'GET' });
  }

  public getWorkerTraining(id: string, trainingId: string): Observable<TrainingModel.IWorkerTraining> {
    return this.protectedRequest(`workers/${id}/trainings/${trainingId}`, { method: 'GET' });
  }

  public getWorkerProjectNameList(id: string, companyId: string): Observable<GeneralModel.INamedEntity[]> {
    // return this.protectedRequest(`workers/${id}/projectnames`, { method: 'GET' });
    return this.protectedRequest(`workers/${id}/company/${companyId}/projectnames`, { method: 'GET' });
  }

  public addWorkerCertification(id: string, certification: CertificationModel.IWorkerCertification): Observable<CertificationModel.IWorkerCertification> {
    return this.protectedRequest(`workers/${id}/certifications`, { method: 'POST', body: certification });
  }

  public updateWorkerCertification(
    workerId: string,
    certification: CertificationModel.IWorkerCertification
  ): Observable<CertificationModel.IWorkerCertification> {
    return this.protectedRequest(`workers/${workerId}/certifications/${certification.id}`, { method: 'PUT', body: certification });
  }

  public deleteWorkerCertification(workerId: string, certificationId: string): Observable<CertificationModel.IWorkerCertification> {
    return this.protectedRequest(`workers/${workerId}/certifications/${certificationId}`, { method: 'DELETE' });
  }

  public deleteWorkerCertificationFiles(workerId: string, certificationId: string, files: string[]): Observable<any> {
    return this.protectedRequest(`workers/${workerId}/certifications/${certificationId}/files`, { method: 'DELETE', body: { fileIds: files } });
  }

  public addWorkerTraining(id: string, training: TrainingModel.IWorkerTraining): Observable<TrainingModel.IWorkerTraining> {
    return this.protectedRequest(`workers/trainings/create`, { method: 'POST', body: { ...training, workers: [id] } });
  }

  public updateWorkerTraining(workerId: string, training: TrainingModel.IWorkerTraining): Observable<TrainingModel.IWorkerTraining> {
    return this.protectedRequest(`workers/${workerId}/trainings/${training.id}`, { method: 'PUT', body: training });
  }

  public deleteWorkerTraining(workerId: string, trainingId: string): Observable<TrainingModel.IWorkerTraining> {
    return this.protectedRequest(`workers/${workerId}/trainings/${trainingId}`, { method: 'DELETE' });
  }

  public deleteWorkerTrainingFiles(workerId: string, trainingId: string, files: string[]): Observable<any> {
    return this.protectedRequest(`workers/${workerId}/trainings/${trainingId}/files`, { method: 'DELETE', body: { fileIds: files } });
  }

  public getWorkerCertificationResource(workerId: string, certId: string, list: string[]): Observable<any> {
    return this.protectedRequest(`workers/${workerId}/certifications/${certId}/upload`, { method: 'POST', body: { fileNames: list } });
  }

  public getWorkerTrainingResource(workerId: string, trainingId: string, list: string[]): Observable<any> {
    return this.protectedRequest(`workers/${workerId}/trainings/${trainingId}/upload`, { method: 'POST', body: { fileNames: list } });
  }

  public getConsentForm(workerId: string, projectId: string): Observable<ConsentFormModel.IConsentForm> {
    return this.protectedRequest(`workers/${workerId}/projects/${projectId}/consentForm`, { method: 'GET' });
  }

  public getEditableConsentForm(workerId: string, projectId: string): Observable<ConsentFormModel.IConsentForm> {
    return this.protectedRequest(`workers/${workerId}/projects/${projectId}/projectForm`, { method: 'GET' });
  }

  public saveConsentForm(workerId: string, projectId: string, data: ConsentFormModel.IProjectInformationForm): Observable<ConsentFormModel.IConsentForm> {
    return this.protectedRequest(`workers/${workerId}/projects/${projectId}/projectForm?version=2`, { method: 'PUT', body: data });
  }

  public assignWorkerToCompany(workerId: string, companyId: string): Observable<any> {
    return this.protectedRequest(`workers/${workerId}/companies/${companyId}`, { method: 'POST' });
  }

  public reAssignWorkerToCompany(data: any): Observable<any> {
    return this.protectedRequest(`Workers/ReassignWorkerCompany`, { method: 'POST', body: data });
  }

  public discontinueWorkerCompany(data: any): Observable<any> {
    return this.protectedRequest(`workers/DiscontinueWorkerCompany`, { method: 'POST', body: data });
  }

  public uploadWorkerGovernmentDocuments(workerId: string, fileNames: string[]): Observable<any> {
    return this.protectedRequest(`workers/${workerId}/governmentDocuments/upload`, { method: 'POST', body: { fileNames } });
  }

  public deleteWorkerGovernmentDocuments(workerId: string, governmentDocumentId: string): Observable<any> {
    return this.protectedRequest(`workers/${workerId}/governmentDocuments/${governmentDocumentId}/files`, { method: 'DELETE' });
  }

  public updateBillingEngineSettings(projectId: string, data: any): Observable<any> {
    return this.protectedRequest(`projects/${projectId}/billingEngineSettings`, { method: 'POST', body: data });
  }

  public getBillingEngineSettings(projectId: string): Observable<any> {
    return this.protectedRequest(`projects/${projectId}/billingEngineSettings`, { method: 'GET' });
  }

  public getEthnicities(): Observable<WorkerModel.IEthnicity[]> {
    return this.protectedRequest('ethnicities', { method: 'GET' });
  }

  public getLanguages(): Observable<WorkerModel.ILanguage[]> {
    return this.protectedRequest('languages', { method: 'GET' });
  }

  public getJobTitles(): Observable<WorkerModel.IJobTitle[]> {
    return this.protectedRequest('jobTitles', { method: 'GET' });
  }

  public getSkilledTrades(): Observable<WorkerModel.ISkilledTrade[]> {
    return this.protectedRequest('skilledTrades', { method: 'GET' });
  }

  public getIdentificationTypes(): Observable<WorkerModel.IIdentificationType[]> {
    return this.protectedRequest('identificationTypes', { method: 'GET' });
  }

  public getSocJobTitles(): Observable<WorkerModel.ISOCJobTitle[]> {
    return this.protectedRequest('socJobTitles', { method: 'GET' });
  }

  public getWorkerObservation(workerId: string, id: string): Observable<WorkerModel.IWorkerObservation> {
    return this.protectedRequest(`workers/${workerId}/observations/${id}`, { method: 'GET' });
  }

  public downloadConsentForm(id: string, projectId: string): Observable<FileModel.IDownloadFile> {
    return ApiService.AuthService.getSession().pipe(
      mergeMap(token => this.downloadFile(`workers/${id}/projects/${projectId}/consentForm/pdf`, 'pdf', token.getIdToken().getJwtToken()))
    );
  }

  public getGeographicLocationsList(): Observable<WorkerModel.IGeographicLocation[]> {
    return this.protectedRequest('geographicLocations', { method: 'GET' });
  }

  public getTradeStatuses(): Observable<WorkerModel.ITradeStatus[]> {
    return this.protectedRequest('tradeStatus', { method: 'GET' });
  }

  public getLanguageTurnerProtocols(): Observable<WorkerModel.ILanguageTurnerProtocol[]> {
    return this.protectedRequest('languageTurnerProtocol', { method: 'GET' });
  }

  public getWorkerControlSettings(workerId: string): Observable<WorkerModel.IWorkerControlSettings> {
    return this.protectedRequest(`workers/${workerId}/workercontrolsettings`, { method: 'GET' });
  }

  // Communications
  public getProjectCommunications(projectId: string, query?: GeneralModel.IQueryParams): Observable<ICommunicationsResponse> {
    return this.protectedRequest(`projects/${projectId}/communications${query ? `?${parseQuery(sanitizePaginationQuery(query))}` : ''}`, {
      method: 'GET',
    });
  }

  public sendCommunication(body: object): Observable<any> {
    return this.protectedRequest(`communications`, {
      method: 'POST',
      body: body,
    });
  }

  public sendCommunicationConfirmation(communicationId: string): Observable<any> {
    return this.protectedRequest(`communications/${communicationId}/confirm`, {
      method: 'POST',
    });
  }

  public updateNewRepliesCount(): Observable<any> {
    return this.protectedRequest(`users/preferences`, {
      method: 'POST',
    });
  }

  // User
  public validateToken(token: string): Observable<GeneralModel.ITokenResponse> {
    return this.publicRequest('users/validateToken', { method: 'POST', body: { token } });
  }

  public createAccount(token: string, password: string): Observable<UserModel.IUser> {
    return this.publicRequest('users/register', { method: 'POST', body: { token, password } });
  }

  public getUserList(query: GeneralModel.IQueryParams): Observable<GeneralModel.IPagination<Partial<UserModel.IUser>>> {
    return this.protectedRequest(`users?${parseQuery(sanitizePaginationQuery(query))}`, { method: 'GET' });
  }

  public getGroupList(query: any, isDataProtectionDisabled: boolean): Observable<any> {
    return this.securityRequest('groups/search/internal?isDataProtectionDisabled=' + isDataProtectionDisabled, { method: 'POST', body: query });
  }

  public getAdminPermission(companyUserId: string): Observable<any> {
    return this.securityRequest(`companyusers/${companyUserId}/haspermission?permissionName=Admin.General`, { method: 'GET' });
  }

  public getUserRoles(): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest('projectRoles', { method: 'GET' });
  }

  public getUserPermissions(companyUserId: string): Observable<any> {
    return this.securityRequest(
      `companyusers/${companyUserId}/availablepermissions?IncludeDirectPermissions=true&IncludeGroupPermissions=true&IncludeScopePermissions=true&MakeItFlat=true`,
      {
        method: 'GET',
      }
    );
  }

  public initiateAuth(userName: string, clientId: string): Observable<any> {
    return this.securityRequest('Users/initiateauth', {
      method: 'POST',
      body: {
        username: userName,
        clientId: clientId,
        customauthchallenge: '1',
      },
    });
  }

  public respondToAuthChallenge(userName: string, clientId: string, selectedCompany: string, authResponse: any): Observable<any> {
    return this.securityRequest('Users/respondtoauthchallenge', {
      method: 'POST',
      body: {
        username: userName,
        clientId: clientId,
        SelectedCompany: selectedCompany,
        customauthchallenge: '1',
        session: authResponse.session,
      },
    });
  }

  public resetPassword(email: string, mobilePhoneNumber: string): Observable<any> {
    return this.publicRequest('users/resetPassword', { method: 'POST', body: { email, mobilePhoneNumber } });
  }

  public confirmResetPassword(token: string, email: string, password: string, mobilePhoneNumber: string): Observable<any> {
    return this.publicRequest('users/confirmResetPassword', { method: 'POST', body: { email, token, password, mobilePhoneNumber } });
  }

  public validateResetPasswordToken(token: string, email: string, mobilePhoneNumber: string): Observable<any> {
    return this.publicRequest('users/validateResetPasswordToken', { method: 'POST', body: { email, token, mobilePhoneNumber } });
  }

  public fetchUserContact(userId: string, type: number): Observable<any> {
    return this.protectedRequest(`users/${userId}/contacts?type=${type}`, { method: 'GET' });
  }

  public createUserContact(userId: string, type: number, value: string): Observable<any> {
    return this.protectedRequest(`users/${userId}/contacts`, { method: 'POST', body: { contactType: type, contactValue: value } });
  }

  public verifyUserContact(userId: string, contactId: string): Observable<any> {
    return this.protectedRequest(`users/${userId}/contacts/${contactId}/verify`, { method: 'POST' });
  }

  public makePrimaryUserContact(userId: string, contactId: string): Observable<any> {
    return this.protectedRequest(`users/${userId}/contacts/${contactId}/setasprimary`, { method: 'POST' });
  }

  public removeUserContact(userId: string, contactId: string): Observable<any> {
    return this.protectedRequest(`users/${userId}/contacts/${contactId}`, { method: 'DELETE' });
  }

  public validateUser(token: string): Observable<any> {
    return this.publicRequest(`users/validateUserContactToken`, { method: 'POST', body: { token } });
  }

  public approveUser(userId: string, companyId: string, projectId: string): Observable<any> {
    return this.protectedRequest(`users/${userId}/Company/${companyId}/Project/${projectId}/ApproveUserApproval`, { method: 'POST', body: {} });
  }

  public denyUser(userId: string, companyId: string, projectId: string): Observable<any> {
    return this.protectedRequest(`users/${userId}/Company/${companyId}/Project/${projectId}/DenyUserApproval`, { method: 'POST', body: {} });
  }

  public changePassword(currentPassword: string, newPassword: string): Observable<any> {
    return ApiService.AuthService.getSession().pipe(
      mergeMap(token =>
        this.protectedRequest(`users/self/changePassword`, {
          method: 'PUT',
          body: { accessToken: token.getAccessToken().getJwtToken(), currentPassword, newPassword },
        })
      )
    );
  }

  public updateProfile(data): Observable<any> {
    return this.protectedRequest('users/self', { method: 'PUT', body: data });
  }

  public getFCAAdmins(query?: GeneralModel.IQueryParams): Observable<any> {
    // return this.protectedRequest(`users/fcaSuperAdmins`, { method: 'GET' });
    return this.protectedRequest(`users/fcaDirectors`, { method: 'GET' });
  }

  public printPDF(badgeId: string): Observable<FileModel.IDownloadFile> {
    return ApiService.AuthService.getSession().pipe(
      mergeMap(token => this.downloadFile(`badges/users/${badgeId}/pdf`, 'pdf', token.getIdToken().getJwtToken()))
    );
  }

  public getStatusProcore(): Observable<any> {
    return this.protectedRequest('integrations/proCore/status', { method: 'GET' });
  }

  public getReportFrequency(clientId: string): Observable<any> {
    return this.protectedRequest(`integrations/procore/companies/${clientId}/settings`, { method: 'GET' });
  }

  public saveReportFrequency(clientId: string, frequency: number): Observable<any> {
    const data = {
      reportingFrequency: frequency,
    };
    return this.protectedRequest(`integrations/procore/companies/${clientId}/settings`, { method: 'PUT', body: data });
  }

  public connectProcore(data): Observable<any> {
    return this.protectedRequest('integrations/proCore/connect', {
      method: 'POST',
      body: { clientId: data.data.clientID, clientSecret: data.data.clientSecret },
    });
  }

  public disconnectProcore(): Observable<any> {
    return this.protectedRequest('integrations/proCore/disconnect', { method: 'DELETE' });
  }

  public getProcoreProjects(clientId: string): Observable<ProcoreModel.IProcoreProjects> {
    return this.protectedRequest(`integrations/procore/companies/${clientId}/projects`, { method: 'GET' });
  }

  public getProcoreProjectMappings(clientId: string): Observable<ProcoreModel.IProcoreProjectMappings> {
    return this.protectedRequest(`integrations/procore/companies/${clientId}/projectMappings`, { method: 'GET' });
  }

  public getProcoreVendors(clientId: string): Observable<ProcoreModel.IProcoreVendors> {
    return this.protectedRequest(`integrations/procore/companies/${clientId}/vendors`, { method: 'GET' });
  }

  public getProcoreVendorMappings(clientId: string): Observable<ProcoreModel.IProcoreVendorMappings> {
    return this.protectedRequest(`integrations/procore/companies/${clientId}/vendorMappings`, { method: 'GET' });
  }

  public saveProcoreProjectMappings(clientId: string, mappings: IProcoreSaveProjectMapping[]): Observable<any> {
    return this.protectedRequest(`integrations/procore/companies/${clientId}/projectMappings`, { method: 'PUT', body: { projectMappings: mappings } });
  }

  public saveProcoreVendorMappings(clientId: string, mappings: IProcoreSaveVendorMapping[]): Observable<any> {
    return this.protectedRequest(`integrations/procore/companies/${clientId}/vendorMappings`, { method: 'PUT', body: { vendorMappings: mappings } });
  }

  public getProcoreClients(): Observable<any> {
    return this.protectedRequest('integrations/procore/companies', { method: 'GET' });
  }

  public getAccount(): Observable<UserModel.IAccount> {
    return this.protectedRequest('users/self', { method: 'GET' });
  }

  public getProfilePhotoResource(fileName: string): Observable<FileModel.IS3FileResponse> {
    return this.protectedRequest(`users/self/uploadPicture`, { method: 'POST', body: { fileName } });
  }

  public uploadWorkerPhoto(workerId: string, fileName: string): Observable<any> {
    return this.protectedRequest(`Workers/${workerId}/UploadPicture`, { method: 'POST', body: { fileName } });
  }

  public changeFcaUserStatus(companyId: string, companyUserId: string, enable: boolean): Observable<any> {
    return this.protectedRequest(`companies/${companyId}/users/${companyUserId}/changestatus`, { method: 'PUT', body: { enable } });
  }

  // CompanyUsers
  public saveSelfWorker(worker: WorkerModel.IWorker): Observable<WorkerModel.IWorker> {
    return this.protectedRequest('companyUsers/self/workers', { method: 'POST', body: worker });
  }

  public acceptProjectInvitation(id: string, paymentMethodId: string): Observable<any> {
    return this.protectedRequest(`projects/${id}/accept`, { method: 'POST', body: { paymentMethodId } });
  }

  public getSelfWorkerList(query: GeneralModel.IQueryParams): Observable<GeneralModel.IPagination<WorkerModel.IWorker>> {
    return this.protectedRequest(`companyUsers/self/workers?${parseQuery(sanitizePaginationQuery(query))}`, { method: 'GET' });
  }

  public getSelfWorkerStatistics(): Observable<StatisticsModel.IWorkerStatistics> {
    return this.protectedRequest('companyUsers/self/statistics/worker', { method: 'GET' });
  }

  public updateCompanyUserProfile(data): Observable<any> {
    return this.protectedRequest('companyUsers/self', { method: 'PUT', body: data });
  }

  public getCompanyUserAccount(): Observable<UserModel.IAccount> {
    return this.protectedRequest('companyUsers/self', { method: 'GET' });
  }

  public deleteUser(companyId: string, companyUserId: string): Observable<UserModel.IAccount> {
    return this.protectedRequest(`companies/${companyId}/users/${companyUserId}`, { method: 'DELETE' });
  }

  // Access Control Systems
  public getAccessControlSystem(id: string): Observable<AccessControlSystemModel.IAccessControlSystem> {
    return this.protectedRequest(`accessControlSystems/${id}`, { method: 'GET' });
  }

  public getAccessControlSystemList(
    query: GeneralModel.IQueryParams
  ): Observable<GeneralModel.IPagination<Partial<AccessControlSystemModel.IAccessControlSystem>>> {
    return this.protectedRequest(`accessControlSystems?${parseQuery(sanitizePaginationQuery(query))}`, {
      method: 'GET',
    });
  }

  public getAccessControlSystemSummary(id: string): Observable<AccessControlSystemModel.IAccessControlSystem> {
    return this.protectedRequest(`accessControlSystems/${id}/summary`, { method: 'GET' });
  }

  public deleteAccessControlSystem(id: string): Observable<any> {
    return this.protectedRequest(`accessControlSystems/${id}`, { method: 'DELETE' });
  }

  public saveAccessControlSystemAccessPoint(
    accessControlSystem: AccessControlSystemModel.IAccessControlSystem
  ): Observable<AccessControlSystemModel.IAccessControlSystem> {
    return this.protectedRequest('accessControlSystems/accessPoint', { method: 'POST', body: accessControlSystem });
  }

  public saveAccessControlSystemHandheld(
    accessControlSystem: AccessControlSystemModel.IAccessControlSystem
  ): Observable<AccessControlSystemModel.IAccessControlSystem> {
    return this.protectedRequest('accessControlSystems/handheld', { method: 'POST', body: accessControlSystem });
  }

  public updateAccessControlSystemAccessPoint(
    accessControlSystem: AccessControlSystemModel.IAccessControlSystem
  ): Observable<AccessControlSystemModel.IAccessControlSystem> {
    return this.protectedRequest(`accessControlSystems/accessPoint/${accessControlSystem.id}`, { method: 'PUT', body: accessControlSystem });
  }

  public updateAccessControlSystemHandheld(
    accessControlSystem: AccessControlSystemModel.IAccessControlSystem
  ): Observable<AccessControlSystemModel.IAccessControlSystem> {
    return this.protectedRequest(`accessControlSystems/handheld/${accessControlSystem.id}`, { method: 'PUT', body: accessControlSystem });
  }

  public addACSDates(acs: AccessControlSystemModel.IACSDates): Observable<AccessControlSystemModel.IAccessControlSystem[]> {
    return this.protectedRequest(`accessControlSystems/dates`, { method: 'POST', body: acs });
  }

  public getFcaTechPackageList(): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest(`fcaTechPackageItems`, { method: 'GET' });
  }

  public getHardwareTypeList(): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest(`accessPointHardwareTypes`, { method: 'GET' });
  }

  // Badge Printing Systems
  public getBadgePrinterSystemList(query: GeneralModel.IQueryParams): Observable<GeneralModel.IPagination<BadgePrintingSystemModel.IBadgePrintingSystem>> {
    return this.protectedRequest(`badgePrintingSystems?${parseQuery(sanitizePaginationQuery(query))}`, { method: 'GET' });
  }

  public getBadgePrinterSystem(id: string): Observable<BadgePrintingSystemModel.IBadgePrintingSystem> {
    return this.protectedRequest(`badgePrintingSystems/${id}`, { method: 'GET' });
  }

  public deleteBadgePrinterSystem(id: string): Observable<BadgePrintingSystemModel.IBadgePrintingSystem> {
    return this.protectedRequest(`badgePrintingSystems/${id}`, { method: 'DELETE' });
  }

  public saveBadgePrinterSystem(badgePrinterSystem: BadgePrintingSystemModel.IBadgePrintingSystem): Observable<BadgePrintingSystemModel.IBadgePrintingSystem> {
    return this.protectedRequest('badgePrintingSystems', { method: 'POST', body: badgePrinterSystem });
  }

  public updateBadgePrinterSystem(
    badgePrinterSystem: BadgePrintingSystemModel.IBadgePrintingSystem
  ): Observable<BadgePrintingSystemModel.IBadgePrintingSystem> {
    return this.protectedRequest(`badgePrintingSystems/${badgePrinterSystem.id}`, { method: 'PUT', body: badgePrinterSystem });
  }

  public addBPSDates(bps: BadgePrintingSystemModel.IBPSDates): Observable<AccessControlSystemModel.IAccessControlSystem[]> {
    return this.protectedRequest(`badgePrintingSystems/dates`, { method: 'POST', body: bps });
  }

  // Statistics
  public getProjectStatistics(): Observable<StatisticsModel.IProjectStatistics> {
    return this.protectedRequest('statistics/projects', { method: 'GET' });
  }

  public getCompanyProjectStatistics(): Observable<StatisticsModel.IProjectStatistics> {
    return this.protectedRequest('statistics/companyprojects', { method: 'GET' });
  }

  public getClientStatistics(): Observable<StatisticsModel.IResourceStatistics> {
    return this.protectedRequest('statistics/companies', { method: 'GET' });
  }

  public getInventoryStatistics(): Observable<StatisticsModel.IInventoryStatistics> {
    return this.protectedRequest('statistics/inventory', { method: 'GET' });
  }

  public getInvoiceStatistics(): Observable<StatisticsModel.IInvoiceStatistics> {
    return this.protectedRequest('statistics/invoices', { method: 'GET' });
  }

  public getWorkerStatistics(): Observable<StatisticsModel.IWorkerStatistics> {
    return this.protectedRequest('statistics/workers', { method: 'GET' });
  }

  public getNewBadges(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ITodayWidgetStatistics> {
    return this.protectedRequest(`statistics/newBadges`, { method: 'POST', body: query });
  }

  public getProjectWidgetStatistics(payload: any): Observable<StatisticsModel.ILinePieWidgetStatistics> {
    return this.protectedRequest(`statistics/projects/widget`, { method: 'POST', body: { ...payload } });
  }

  public getClientWidgetStatistics(payload: any): Observable<StatisticsModel.IPieWidgetStatistics> {
    return this.protectedRequest(`statistics/companies/widget`, { method: 'POST', body: { ...payload } });
  }

  public getGrossRevenueStatistics(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ITodayWidgetStatistics> {
    return this.protectedRequest('statistics/grossRevenue', { method: 'POST', body: query });
  }

  public getProjectDetailStatistics(id: string): Observable<StatisticsModel.IProjectDetailStatistics> {
    return this.protectedRequest(`statistics/projects/${id}`, { method: 'GET' });
  }

  public getWorkersActivityStatistics(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ITodayWidgetStatistics> {
    return this.protectedRequest('statistics/workersActivity', { method: 'POST', body: query });
  }

  public getClientDetailStatistics(id: string): Observable<StatisticsModel.IClientDetailStatistics> {
    return this.protectedRequest(`statistics/companies/${id}`, { method: 'GET' });
  }

  public getGrossRevenueWidgetStatistics(payload: any): Observable<StatisticsModel.ILineWidgetStatistics> {
    return this.protectedRequest(`statistics/revenue/widget`, { method: 'POST', body: { ...payload } });
  }

  public getClientRevenueWidgetStatistics(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ILineWidgetStatistics> {
    return this.protectedRequest(`companiesStatistics/revenue/widget`, { method: 'POST', body: query });
  }

  public getWorkersActivityWidgetStatistics(payload: any): Observable<StatisticsModel.ILinePieWidgetStatistics> {
    return this.protectedRequest(`statistics/workers/widget`, { method: 'POST', body: { ...payload } });
  }

  public getClientWorkersActivityWidgetStatistics(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ILinePieWidgetStatistics> {
    return this.protectedRequest(`companiesStatistics/workers/widget`, { method: 'POST', body: query });
  }

  public getProjectWorkersActivityStatistics(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ILinePieWidgetStatistics> {
    return this.protectedRequest(`projectStatistics/workersActivity`, { method: 'POST', body: query });
  }

  public getProjectAccessControlSystemStatistics(query: GeneralModel.IQueryParams): Observable<StatisticsModel.IAcsSummaryStatistics> {
    return this.protectedRequest(`projectStatistics/acs`, { method: 'POST', body: query });
  }

  public getProjectBadgePrintingSystemStatistics(query: GeneralModel.IQueryParams): Observable<StatisticsModel.IDeviceStatistics> {
    return this.protectedRequest(`projectStatistics/bps`, { method: 'POST', body: query });
  }

  public getProjectClientsStatistics(query: GeneralModel.IQueryParams): Observable<StatisticsModel.IPieWidgetStatistics> {
    return this.protectedRequest(`projectStatistics/clients`, { method: 'POST', body: query });
  }

  public getProjectActivesStatistics(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ILocationStatistics> {
    return this.protectedRequest(`projectStatistics/activeProjects/maps`, { method: 'POST', body: query });
  }

  public getProjectBadgeLocationStatistics(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ITopTenStatistics[]> {
    return this.protectedRequest(`projectStatistics/newBadgesByLocations`, { method: 'POST', body: query });
  }

  public getProjectBadgeProjectStatistics(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ITopTenStatistics[]> {
    return this.protectedRequest(`projectStatistics/newBadgesByProject`, { method: 'POST', body: query });
  }

  public getProjectRevenueProjectStatistics(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ILineWidgetStatistics> {
    return this.getGrossRevenueWidgetStatistics(query);
  }

  public getProjectTopTenStatistics(query: GeneralModel.IQueryParams): Observable<StatisticsModel.IProjectTopTenStatistics[]> {
    return this.protectedRequest(`projectStatistics/topTenProjects`, { method: 'POST', body: query });
  }

  public getClientTopTenStatistics(query: GeneralModel.IQueryParams): Observable<StatisticsModel.IClientTopTenStatistics[]> {
    return this.protectedRequest(`companiesStatistics/topTenCompanies`, { method: 'POST', body: query });
  }

  public getClientActivesStatistics(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ILocationStatistics> {
    return this.protectedRequest(`companiesStatistics/activeCompanies/maps`, { method: 'POST', body: query });
  }

  public getClientsByTrades(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ITopTenStatistics[]> {
    return this.protectedRequest(`companiesStatistics/byTrades`, { method: 'POST', body: query });
  }

  public getWorkersByProject(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ITopTenStatistics[]> {
    return this.protectedRequest(`workersStatistics/topTenWorkersByProjects`, { method: 'POST', body: query });
  }

  public getNewAssignedWorkersWidgetStatistics(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ILinePieWidgetStatistics> {
    return this.protectedRequest(`workersStatistics/newAssigned`, { method: 'POST', body: query });
  }

  public getWorkersByClient(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ITopTenStatistics[]> {
    return this.protectedRequest(`workersStatistics/topTenWorkersByClients`, { method: 'POST', body: query });
  }

  public getWorkersByLocation(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ITopTenStatistics[]> {
    return this.protectedRequest(`workersStatistics/topTenWorkersByLocations`, { method: 'POST', body: query });
  }

  public getWorkersByJobData(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ITopTenStatistics[]> {
    return this.protectedRequest(`workersStatistics/topTenWorkersByJobData?${parseQuery(query)}`, { method: 'GET' });
  }

  public getWorkersByJobDataByTrades(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ITopTenStatistics[]> {
    return this.protectedRequest(`workersStatistics/topTenWorkersByTrades`, { method: 'POST', body: query });
  }

  public getWorkersByJobDataByExperience(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ITopTenStatistics[]> {
    return this.protectedRequest(`workersStatistics/topTenWorkersByExperience`, { method: 'POST', body: query });
  }

  public getWorkersByTradesMinority(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ITopTenStatistics[]> {
    return this.protectedRequest(`workersStatistics/topTenWorkersByTradesEthnicity`, { method: 'POST', body: { isMinority: true, ...query } });
  }

  public getWorkersByTradesNonMinority(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ITopTenStatistics[]> {
    return this.protectedRequest(`workersStatistics/topTenWorkersByTradesEthnicity`, { method: 'POST', body: { isMinority: false, ...query } });
  }

  public getWorkersCertifications(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ITopTenStatistics[]> {
    return this.protectedRequest(`workersStatistics/topTenWorkersByCertifications`, { method: 'POST', body: query });
  }

  public getWorkersObservations(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ITopTenStatistics[]> {
    return this.protectedRequest(`workersStatistics/topTenWorkersByObservations`, { method: 'POST', body: query });
  }

  public getWorkersByDemographicDataByLanguage(query: GeneralModel.IQueryParams): Observable<StatisticsModel.IPieWidgetStatistics> {
    return this.protectedRequest(`workersStatistics/primaryLanguage`, { method: 'POST', body: query });
  }

  public getWorkersByDemographicDataByEthnicity(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ITopTenStatistics[]> {
    return this.protectedRequest(`workersStatistics/topTenWorkersByEthnicity`, { method: 'POST', body: query });
  }

  public getWorkersByDemographicDataByGender(query: GeneralModel.IQueryParams): Observable<StatisticsModel.IPieWidgetStatistics> {
    return this.protectedRequest(`workersStatistics/gender`, { method: 'POST', body: query });
  }

  public getWorkersTrainings(query: GeneralModel.IQueryParams): Observable<StatisticsModel.IPieWidgetStatistics> {
    return this.protectedRequest(`workersStatistics/training`, { method: 'POST', body: query });
  }

  public getWorkersNewWorkers(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ILinePieWidgetStatistics> {
    return this.getWorkersActivityWidgetStatistics(query);
  }

  public getLaborCensus(query: GeneralModel.IQueryParams): Observable<StatisticsModel.ILaborCensus> {
    return this.protectedRequest(`workersStatistics/laborcensus`, { method: 'POST', body: query });
  }

  public fetchDashboardFilter(): Observable<any> {
    return this.protectedRequest(`DashboardFilters`, { method: 'GET' });
  }

  public saveDashboardFilter(payload: StatisticsModel.IDashboardFilter): Observable<StatisticsModel.IDashboardFilter[]> {
    return this.protectedRequest(`DashboardFilters`, { method: 'POST', body: payload });
  }

  public deleteDashboardFilter(name: string): Observable<StatisticsModel.IDashboardFilter[]> {
    return this.protectedRequest(`DashboardFilters`, { method: 'DELETE', body: { name } });
  }

  public updateDashboardFilter(payload: StatisticsModel.IDashboardFilter): Observable<StatisticsModel.IDashboardFilter> {
    return this.protectedRequest(`DashboardFilters`, { method: 'PUT', body: payload });
  }

  public getDashboardProjects(): Observable<StatisticsModel.IDashboardProject[]> {
    return this.protectedRequest(`dashboardFilters/projects`, { method: 'GET' });
  }

  public getWorkForceMonitoringData(query: GeneralModel.IQueryParams): Observable<StatisticsModel.IWorkForceMonitoringData> {
    return this.protectedRequest(`dashboards/gcview`, { method: 'POST', body: query });
  }

  public getDailyLogData(query: GeneralModel.IQueryParams): Observable<StatisticsModel.IDailyLogData> {
    return this.protectedRequest(`dashboards/dailyLogGcView`, { method: 'POST', body: query });
  }

  // Downloads
  public getDashboardWidgetDownloads(query: GeneralModel.IQueryParams): Observable<DownloadsModel.IDashboardWidgetDownloadsResponse> {
    return this.protectedRequest(`users/self/exportStatus?${parseQuery(query)}`, { method: 'GET', body: query });
  }

  public getPricingAgreementDownloads(query: GeneralModel.IQueryParams): Observable<any> {
    return this.protectedRequest(`users/self/pricingAgreements/downloads?${parseQuery(query)}`, { method: 'GET' });
  }

  // Badge
  public getBadge(id: string): Observable<BadgeModel.IBadge> {
    return this.protectedRequest(`badges/${id}`, { method: 'GET' });
  }

  public updateBadge(id: string, badge: BadgeModel.IBadgeUpdateRequest): Observable<any> {
    return this.protectedRequest(`badges/${id}`, { method: 'PUT', body: badge });
  }

  public activateBadge(id: string, tagId: any, billingAddressId?: string, reason?: string): Observable<BadgeModel.IBadge> {
    return this.protectedRequest(`badges/${id}/activate`, { method: 'POST', body: { tagId, id, billingAddressId, statusChangeReason: reason } });
  }
  public revokeBadge(id: string, reason: string): Observable<BadgeModel.IBadge> {
    return this.protectedRequest(`badges/${id}/revoke`, { method: 'POST', body: { statusChangeReason: reason } });
  }

  public deactivateBadge(id: string, reasonObj: { reason: string; reasonId: string }): Observable<BadgeModel.IBadge> {
    return this.protectedRequest(`badges/${id}/deactivate`, {
      method: 'POST',
      body: { statusChangeReason: reasonObj?.reason, badgeActionReasonId: reasonObj?.reasonId },
    });
  }

  public getVisitorBadge(badgeId: string): Observable<BadgeModel.IBadge> {
    return this.protectedRequest(`badges/visitors/${badgeId}`, { method: 'GET' });
  }

  public updateBadgeVisitor(id: string, badgeVisitor: BadgeModel.IBadgeVisitor): Observable<any> {
    return this.protectedRequest(`badges/visitors/${id}`, { method: 'PUT', body: { ...badgeVisitor, id } });
  }

  public getBadgeHistory(id: string, query: GeneralModel.IQueryParams, type: string): Observable<GeneralModel.IPagination<BadgeModel.IBadgeHistory>> {
    return this.protectedRequest(`badges/${type === 'visitor' ? 'visitors/' : ''}${id}/history?${parseQuery(sanitizePaginationQuery(query))}`, {
      method: 'GET',
    });
  }

  public getPrintableWorkerBadge(
    badgeId: string,
    projectBadgeTemplateId: string,
    trackHistory?: boolean,
    isSampleWorker?: boolean
  ): Observable<FileModel.IDownloadFile> {
    return ApiService.AuthService.getSession().pipe(
      mergeMap(token =>
        this.downloadFile(
          `badges/${badgeId}/pdf?projectBadgeTemplateId=${projectBadgeTemplateId}${trackHistory === false ? '&trackHistory=false' : ''}${
            isSampleWorker === true ? '&isSampleWorker=true' : ''
          }`,
          'pdf',
          token.getIdToken().getJwtToken()
        )
      )
    );
  }

  public getPrintableVisitorBadge(badgeId: string, projectBadgeTemplateId: string, trackHistory: boolean): Observable<FileModel.IDownloadFile> {
    return ApiService.AuthService.getSession().pipe(
      mergeMap(token =>
        this.downloadFile(
          `badges/visitors/${badgeId}/pdf?projectBadgeTemplateId=${projectBadgeTemplateId}${trackHistory === false ? '&trackHistory=false' : ''}`,
          'pdf',
          token.getIdToken().getJwtToken()
        )
      )
    );
  }

  public previewBadgeTemplate(file: any): Observable<any> {
    return ApiService.AuthService.getSession().pipe(
      mergeMap(token => this.uploadFileOnProtectedRoute('badges/pdf', file, 'pdf', token.getIdToken().getJwtToken()))
    );
  }

  public resyncWorkerBadge(badgeId: string): Observable<any> {
    return this.protectedRequest(`badges/${badgeId}/resync`, { method: 'POST', body: {} });
  }

  public getBadgeDeactivationReasons(): Observable<any> {
    return this.protectedRequest(`badgedeactivationreasons`, { method: 'GET' });
  }

  public getBadgeTypes(): Observable<BadgeModel.IBadgeType[]> {
    return this.protectedRequest(`BadgeTypes`, { method: 'GET' });
  }

  public getLocationsByBadgeId(badgeId: string): Observable<BadgeModel.ILocation[]> {
    return this.protectedRequest(`locationmanager/badges/${badgeId}/locationstatus`, { method: 'GET' });
  }

  public resetLocations(body: any): Observable<any> {
    return this.protectedRequest(`locationmanager/resetlocations`, { method: 'POST', body });
  }

  // public getWorkerBadgeRequirements(workerId: string, projectId: string): Observable<BadgeModel.IWorkerBadgeRequirements> {
  //   return this.protectedRequest(`workers/${workerId}/projects/${projectId}/requirements`, { method: 'GET' });
  // }

  public getWorkerBadgeRequirements(workerId: string, companyId: string, projectId: string): Observable<BadgeModel.IWorkerBadgeRequirements> {
    return this.protectedRequest(`workers/${workerId}/company/${companyId}/projects/${projectId}/requirements`, { method: 'GET' });
  }

  // Dashboard
  public getExpandedWorkers(query: GeneralModel.IQueryParams): Observable<StatisticsModel.IExpandedWorkersOnsiteResponse> {
    return this.protectedRequest(`dashboards/workerDetails`, { method: 'POST', body: query });
  }

  public getExpandedCompanies(query: GeneralModel.IQueryParams): Observable<StatisticsModel.IExpandedCompaniesOnsiteResponse> {
    return this.protectedRequest(`dashboards/companyDetails`, { method: 'POST', body: query });
  }

  public getExpandedTrades(query: GeneralModel.IQueryParams): Observable<StatisticsModel.IExpandedTradesOnsiteResponse> {
    return this.protectedRequest(`dashboards/tradeDetails`, { method: 'POST', body: query });
  }

  public exportWidgetData(query: GeneralModel.IQueryParams): Observable<any> {
    return this.protectedRequest(`exports`, { method: 'POST', body: query });
  }

  // Payment
  public getPaymentMethodsSelf(): Observable<PaymentModel.IPaymentMethod[]> {
    return this.protectedRequest('paymentMethod/self', { method: 'GET' });
  }

  public createPaymentSelf(id: string, doNotReplaceInProjects: boolean = false): Observable<PaymentModel.IPaymentMethod> {
    return this.protectedRequest('paymentMethod/self', { method: 'POST', body: { paymentMethodId: id, doNotReplaceInProjects } });
  }

  public deletePaymentSelf(id: string): Observable<PaymentModel.IPaymentMethod> {
    return this.protectedRequest(`paymentMethod/self/${id}`, { method: 'DELETE' });
  }

  public replacePaymentSelf(toDeleteId: string, toReplaceWithId: string): Observable<PaymentModel.IPaymentMethod> {
    return this.protectedRequest('paymentMethod/self/replace', {
      method: 'PUT',
      body: { oldPaymentMethodId: toDeleteId, newPaymentMethodId: toReplaceWithId },
    });
  }

  public getPaymentMethods(): Observable<PaymentModel.IPaymentMethod[]> {
    return this.protectedRequest('paymentMethod', { method: 'GET' });
  }

  public createPayment(
    body: { paymentMethodId: string; doNotReplaceInProjects: boolean } | { paymentMethodId: string; isCalledByFcaAdmin: boolean; companyId: string }
  ): Observable<PaymentModel.IPaymentMethod> {
    return this.protectedRequest('paymentMethod', { method: 'POST', body: body });
  }

  public deletePayment(id: string): Observable<PaymentModel.IPaymentMethod> {
    return this.protectedRequest(`paymentMethod/${id}`, { method: 'DELETE' });
  }

  public replacePayment(
    body:
      | { toDeleteId: string; toReplaceWithId: string }
      | { oldPaymentMethodId: string; newPaymentMethodId: string; isCalledByFcaAdmin?: boolean; companyId?: string }
  ): Observable<PaymentModel.IPaymentMethod> {
    return this.protectedRequest('paymentMethod/replace', {
      method: 'PUT',
      body: body,
    });
  }

  public processFailedPayments(
    companyId: string,
    oldPaymentMethodId: string,
    paymentMethodId: string,
    isFromProjectAssign?: boolean
  ): Observable<PaymentModel.IProcessFailedTransactionsResponse> {
    return this.protectedRequest('FailedTransactions/processPayments', {
      method: 'POST',
      body: { companyId, oldPaymentMethodId, paymentMethodId, isFromProjectAssign },
    });
  }

  public getOutstandingInvoicesAmount(companyId: string, paymentMethodId: string): Observable<number> {
    return this.protectedRequest('PaymentMethod/getOutstandingInvoicesAmount', {
      method: 'POST',
      body: { companyId, paymentMethodId },
    });
  }

  public getPaymentMethodForSpecifiedClient(companyId: string): Observable<PaymentModel.IPaymentMethod[]> {
    return this.protectedRequest(`paymentMethod/getCompanyPaymentMethods/${companyId}`, { method: 'GET' });
  }

  public deletePaymentMethodFromSpecifiedClient(id: string, companyId: string): Observable<PaymentModel.IPaymentMethod> {
    return this.protectedRequest(`paymentMethod/deletePaymentMethod/${id}/company/${companyId}`, { method: 'DELETE' });
  }

  public assignProjectsToCreditCard(params: { paymentMethodId: string; companyId: string; projectIds: string[] }): Observable<PaymentModel.IPaymentMethod> {
    return this.protectedRequest('paymentMethod/updatePaymentMethodProjects', {
      method: 'PUT',
      body: params,
    });
  }

  public verifyCvc(params: { paymentMethodId: string; companyId: string; projectName: string; cvc: string }): Observable<any> {
    return this.protectedRequest('paymentMethod/paymentMethodCvcCheck', {
      method: 'POST',
      body: params,
    });
  }

  // Onboarding
  public getOnboardingClient(id: string): Observable<ClientModel.IClient> {
    return this.protectedRequest(`onboardingcompanies/${id}`, { method: 'GET' });
  }

  public updateOnboardingClient(client: ClientModel.IClient): Observable<ClientModel.IClient> {
    return this.protectedRequest(`onboardingcompanies/${client.id}`, { method: 'PUT', body: client });
  }

  public sendApproveOnboardingClient(id: string): Observable<any> {
    return this.protectedRequest(`onboardingcompanies/${id}/sendforapproval `, { method: 'POST' });
  }

  public approveOnboardingClient(id: string): Observable<any> {
    return this.protectedRequest(`onboardingcompanies/${id}/approve`, { method: 'POST' });
  }

  public deleteOnboardingClient(id: string): Observable<any> {
    return this.protectedRequest(`onboardingcompanies/${id}`, { method: 'DELETE' });
  }

  // Turnstile Schedule
  public getProjectTurnstileScheduleTimeSlots(projectId: string): Observable<TurnstileScheduleModel.ITimeSlot[]> {
    return this.protectedRequest(`projects/${projectId}/acsscheduletimeslots`, { method: 'GET' });
  }

  public createProjectTurnstileScheduleTimeSlot(body: TurnstileScheduleModel.ITimeSlot): Observable<TurnstileScheduleModel.ITimeSlot> {
    return this.protectedRequest(`projects/${body.projectId}/acsscheduletimeslots`, { method: 'POST', body });
  }

  public updateProjectTurnstileScheduleTimeSlot(body: TurnstileScheduleModel.ITimeSlot): Observable<TurnstileScheduleModel.ITimeSlot> {
    return this.protectedRequest(`projects/${body.projectId}/acsscheduletimeslots/${body.id}`, { method: 'PUT', body });
  }

  public deleteProjectTurnstileScheduleTimeSlot(projectId: string, id: string): Observable<any> {
    return this.protectedRequest(`projects/${projectId}/acsscheduletimeslots/${id}`, { method: 'DELETE' });
  }

  public getProjectTurnstileSchedules(projectId: string): Observable<TurnstileScheduleModel.ITurnstileSchedule[]> {
    return this.protectedRequest(`projects/${projectId}/acsschedules`, { method: 'GET' });
  }

  public createProjectTurnstileSchedules(
    projectId: string,
    body: TurnstileScheduleModel.ITurnstileSchedule[]
  ): Observable<TurnstileScheduleModel.ITurnstileSchedule[]> {
    return this.protectedRequest(`projects/${projectId}/acsschedules/bulk`, { method: 'POST', body: { daysOfWeek: body } });
  }

  public turnOnProjectTurnstileSchedules(projectId: string): Observable<any> {
    return this.protectedRequest(`projects/${projectId}/acsschedules/turnon`, { method: 'POST' });
  }

  public turnOffProjectTurnstileSchedules(projectId: string, body: TurnstileScheduleModel.IScheduleStatePayload): Observable<any> {
    return this.protectedRequest(`projects/${projectId}/acsschedules/turnoff`, { method: 'POST', body: body });
  }

  // General
  public getCountryList(): Observable<GeneralModel.INamedEntity[]> {
    return this.publicRequest('countries', { method: 'GET' });
  }

  public quickSearch(search: SearchModel.ISearchParams): Observable<GeneralModel.IPagination<SearchModel.IResponse | SearchModel.IWorker>> {
    return this.protectedRequest(`quickSearch/?${parseQuery(sanitizePaginationQuery(search))}`, { method: 'GET' });
  }

  public quickSearchConsolidated(search: SearchModel.ISearchParams): Observable<GeneralModel.IPagination<SearchModel.IResponse | SearchModel.IWorker>> {
    return this.protectedRequest(`quickSearch/consolidated/?${parseQuery(sanitizePaginationQuery(search))}`, { method: 'GET' });
  }

  public getFeatureList(): Observable<GeneralModel.INamedEntity[]> {
    return this.configurationRequest('featureFlags', { method: 'GET' });
  }

  // Product Categories
  public getProductCategories(): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest('productcategories', { method: 'GET' });
  }

  public getQuickBooksAccounts(): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest('quickBooksAccounts', { method: 'GET' });
  }
  // Product Billing Triggers
  public getProductBillingTriggers(): Observable<GeneralModel.INamedEntity[]> {
    return this.protectedRequest('productBillingTriggers', { method: 'GET' });
  }

  // Products
  public getProductList(search?: SearchModel.ISearchParams): Observable<ProductModel.IProduct[]> {
    return this.protectedRequest(`products?${parseQuery(search || {})}`, { method: 'GET' });
  }

  public saveProduct(payload: any): Observable<ProductModel.IProduct> {
    return this.protectedRequest('products', { method: 'POST', body: payload });
  }

  public updateProduct(id: string, payload: any): Observable<ProductModel.IProduct> {
    return this.protectedRequest(`products/${id}`, { method: 'PUT', body: payload });
  }

  public deleteProduct(id: string): Observable<ProductModel.IProduct> {
    return this.protectedRequest(`products/${id}`, { method: 'DELETE' });
  }

  public retireProduct(id: string, body: { toStatus: number }): Observable<ProductModel.IProduct> {
    return this.protectedRequest(`products/${id}/changestatus`, { method: 'PUT', body });
  }

  public getProduct(id: string): Observable<ProductModel.IProduct> {
    return this.protectedRequest(`products/${id}`, { method: 'GET' });
  }

  // Unverified Companies
  public getClientsAllowedToCreateUnverifiedClients(id: string): Observable<string[]> {
    return this.protectedRequest(`projects/${id}/allowedToCreateUnverifiedClientCompanies`, { method: 'GET' });
  }

  public addUnverifiedCompanies(body: ClientModel.IUnverifiedClient): Observable<ClientModel.IClient> {
    return this.protectedRequest(`unverifiedCompanies`, { method: 'POST', body: body });
  }

  public updateUnverifiedCompanySponsor(clientId: string, projectId: string, sponsorId: string): Observable<ClientModel.IClient> {
    return this.protectedRequest(`unverifiedCompanies/${clientId}/projects/${projectId}`, { method: 'PUT', body: { id: clientId, projectId, sponsorId } });
  }

  // Project Companies

  public getProjectCompanies(companyId: string, projectId: string): Observable<ClientModel.IClient> {
    return this.protectedRequest(`projectCompanies/${companyId}?projectId=${projectId}`, { method: 'GET' });
  }

  public updateProjectCompanies(client: ClientModel.IClient): Observable<ClientModel.IClient> {
    return this.protectedRequest(`projectCompanies/${client.id}`, { method: 'PUT', body: client });
  }

  // Audit Trail
  public getChangeLogsSearch(payload: any): Observable<{ paging: any; records: IChangeLogEntity[] }> {
    return this.auditTrailRequest('changeLogs/Search?makeResponseFlat=true', { method: 'POST', body: payload });
  }

  // Files
  public uploadFile(file: FileModel.IFile, url: string): Observable<Partial<FileModel.IFile>> {
    const subject = new Subject<Partial<FileModel.IFile>>();
    const xhr = new XMLHttpRequest(); // rxjs ajax does not support multipart
    xhr.open('PUT', url);
    xhr.upload.onprogress = e => {
      let progress = Math.floor((e.loaded / e.total) * 100);
      progress = Math.min(progress, 99);
      progress = Math.max(progress, 0);
      subject.next({
        uploadId: file.uploadId,
        id: file.id,
        progress,
        status: FileModel.FileStatus.PROGRESS,
      });
    };

    xhr.onload = () => {
      if (xhr.status === 200)
        subject.next({
          uploadId: file.uploadId,
          id: file.id,
          progress: 100,
          status: FileModel.FileStatus.SUCCESS,
        });
      else
        subject.error({
          uploadId: file.uploadId,
          id: file.id,
          progress: null,
          status: FileModel.FileStatus.FAIL,
          error: 'Server Error',
        });
      subject.complete();
    };

    xhr.onerror = () => {
      subject.error({
        uploadId: file.uploadId,
        id: file.id,
        progress: null,
        status: FileModel.FileStatus.FAIL,
        error: 'Server Error',
      });
      subject.complete();
    };

    xhr.send(file.file as any);
    return subject;
  }

  public uploadConfirm(id: string): Observable<any> {
    return this.protectedRequest(`files/${id}/confirm`, { method: 'POST' });
  }

  public downloadFile(path: string, type: string, token?: string): Observable<FileModel.IDownloadFile> {
    const subject = new Subject<FileModel.IDownloadFile>();
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${this.apiUrl}/${path}`);

    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.onload = e => {
      const newFile = new Blob([(e.target as any).response], { type: `application/${type}` });

      if (xhr.status === 200)
        subject.next({
          newFile,
          status: FileModel.FileStatus.SUCCESS,
        });
      else
        subject.error({
          response: {
            title: LANG.EN.ERRORS.DEFAULT,
          },
        });
      subject.complete();
    };

    xhr.onerror = () => {
      subject.error({
        response: {
          title: LANG.EN.ERRORS.DEFAULT,
        },
      });
      subject.complete();
    };

    xhr.responseType = 'blob';

    xhr.send();
    return subject;
  }

  public uploadFileOnProtectedRoute(path: string, file: any, type: string, token?: string): Observable<FileModel.IDownloadFile> {
    const subject = new Subject<FileModel.IDownloadFile>();
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${this.apiUrl}/${path}`);
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    xhr.onload = e => {
      const newFile = new Blob([(e.target as any).response], { type: `application/${type}` });
      if (xhr.status === 200)
        subject.next({
          newFile,
          status: FileModel.FileStatus.SUCCESS,
        });
      else
        subject.error({
          response: {
            title: LANG.EN.ERRORS.DEFAULT,
          },
        });
      subject.complete();
    };

    xhr.onerror = () => {
      subject.error({
        response: {
          title: LANG.EN.ERRORS.DEFAULT,
        },
      });
      subject.complete();
    };

    xhr.responseType = 'blob';
    if (file) {
      xhr.send(file);
    }
    return subject;
  }

  // ApiService
  public protectedRequest<T = any>(path: string, options: IOptionRequest = { method: 'GET' }, mock?: IMock): Observable<T> {
    return ApiService.AuthService.getSession().pipe(mergeMap(token => this.createRequest(path, options, token.getIdToken().getJwtToken(), mock)));
  }

  public publicRequest<T = any>(path: string, options: IOptionRequest = { method: 'GET' }): Observable<T> {
    return this.createRequest(path, options);
  }

  public securityRequest<T = any>(path: string, options: IOptionRequest = { method: 'GET' }, mock?: IMock): Observable<T> {
    return ApiService.AuthService.getSession().pipe(mergeMap(token => this.createRequest(path, options, token.getIdToken().getJwtToken(), mock, 'security')));
  }

  public configurationRequest<T = any>(path: string, options: IOptionRequest = { method: 'GET' }, mock?: IMock): Observable<T> {
    return ApiService.AuthService.getSession().pipe(
      mergeMap(token => this.createRequest(path, options, token.getIdToken().getJwtToken(), mock, 'configuration'))
    );
  }

  public learningRequest<T = any>(path: string, options: IOptionRequest = { method: 'GET' }, mock?: IMock): Observable<T> {
    return ApiService.AuthService.getSession().pipe(mergeMap(token => this.createRequest(path, options, token.getIdToken().getJwtToken(), mock, 'learning')));
  }

  public auditTrailRequest<T = any>(path: string, options: IOptionRequest = { method: 'GET' }, mock?: IMock): Observable<T> {
    return ApiService.AuthService.getSession().pipe(mergeMap(token => this.createRequest(path, options, token.getIdToken().getJwtToken(), mock, 'changeLog')));
  }

  public mockedResponse<T = any>(mock) {
    return new Observable(observer => {
      if (mock.isError) return observer.error(mock);
      return observer.next(mock);
    }).pipe(
      map((data: Partial<IMock>) => data.response as T),
      retryWhen(
        /* istanbul ignore next */ error =>
          error.pipe(
            switchMap(e => {
              e = this.errorFormatter(e);
              if (e.status >= 500 || e.status === 408) return of(e).pipe(delay(this.retryTimeout));
              else return throwError(e);
            })
          )
      )
    );
  }

  public createRequest<T = any>(path: string, options: IOptionRequest, token?: string, mock?: IMock, type?: string): Observable<T> {
    if (token) options.headers = { ...options.headers, Authorization: `Bearer ${token}` };

    if (!!mock) return this.mockedResponse(mock);
    return this.http({
      ...options,
      url: `${
        type
          ? type === 'security'
            ? this.securityApiUrl
            : type === 'configuration'
            ? this.configurationApiUrl
            : type === 'learning'
            ? this.learningApiUrl
            : type === 'changeLog'
            ? this.changeLogApiUrl
            : this.apiUrl
          : this.apiUrl
      }/${path}`,
      headers: {
        'Source-App': 'Freedom-Web',
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...options.headers,
      },
      body: options.body ? this.parseBody(options.body) : undefined,
    }).pipe(
      map(data => data.response as T),
      retryWhen(
        /* istanbul ignore next */ (error: Observable<AjaxResponse>) =>
          error.pipe(
            switchMap((e: AjaxResponse) => {
              e = this.errorFormatter(e);
              if (e.status >= 500 || e.status === 408) return of(e).pipe(delay(this.retryTimeout));
              else return throwError(e);
            }),
            take(this.maxRetries),
            mergeMap(e => throwError(e))
          )
      )
    );
  }

  private parseBody(body: { [key: string]: any }): string {
    try {
      return JSON.stringify(body);
    } catch {
      /* istanbul ignore next line */
      return '';
    }
  }

  private errorFormatter(error) {
    return {
      ...error,
      title: error.response?.message || error.response?.title || LANG.EN.ERRORS.DEFAULT,
      errors: error.response?.errors ? error.response.errors : {},
      response: {
        ...error.response,
        title: error.response?.message || error.response?.title || LANG.EN.ERRORS.DEFAULT,
        errors: error.response?.errors ? error.response.errors : {},
      },
    };
  }

  private sanitizeTimeZoneOffset(timeZoneOffset) {
    if (timeZoneOffset) {
      return timeZoneOffset.replace('+', '');
    }

    return timeZoneOffset;
  }
}
